/*#
# Authorize: getting a token

An agent or a CLI gets its scoped capability token through the platform's
browser loop (tosijs-platform#6): the client starts a request with a
*label* (virta's is machine × repo) and the *caveats* it wants (roles,
collections, methods), a human approves it in a browser, and the client
exchanges a proof for the token. This module is that protocol, ported from
the platform's reference script, with no I/O beyond `fetch` so it runs in
Bun, Node, or a browser. The loopback listener and the token file live in
`bin/login.ts`, which uses it.

Three things matter, and they are the same three the reference names:

1. **The verifier never leaves the process.** Only its SHA-256 is sent
   (PKCE). A consent URL, a browser history entry, or a stolen request id is
   useless on its own.
2. **The token arrives only at the exchange**, over TLS, to whoever holds
   the verifier. It never passes through the browser.
3. **Loopback mode binds 127.0.0.1** and checks the request id coming back
   is the one it started, so a phished approval redirects nowhere useful.
   *Poll* mode has no such binding — the warning it prints is not
   decorative: only approve a URL you generated, on a machine you control.

```
POST /authorize?action=start    { label, caveats, codeChallenge, mode, redirectPort?, ttlMs? }
                                → { status: 'started', requestId, consentUrl, expiresAt, pollIntervalMs }
GET  /authorize?request=<id>    the consent page (a human, signed in)
POST /authorize?action=exchange { requestId, verifier }
                                → { status: 'pending', pollIntervalMs } | { status: 'ready', id, secret, label, caveats, expiresAt }
                                | { status: 'denied' } | { status: 'refused', … }
```

The token itself is `tsp_…`, sent as `Authorization: Bearer`. It attenuates
the approver: effective rights are their live roles ∩ the caveats, it cannot
mint another token, and it lives at most ninety days.
*/

/*{ "parent": "Reference", "order": 96 }*/

import { isWebPage, webPageDiagnosis } from './remote'

export interface Caveats {
  roles: string[]
  /** logical collections it may touch; absent means all of them */
  collections?: string[]
  /** HTTP methods; the host's default is everything but DELETE */
  methods?: string[]
  /** the token's lifetime, ms: the host's default is 30 days, its maximum 90 */
  ttlMs?: number
}

export interface AuthorizeOptions {
  host: string
  /** who this token is: virta uses `<machine> × <repo>` */
  label: string
  caveats: Caveats
  /** `loopback` needs a listener on `redirectPort`; `poll` needs nothing */
  mode: 'loopback' | 'poll'
  redirectPort?: number
  ttlMs?: number
  fetch?: typeof fetch
}

export interface StartedAuthorization {
  requestId: string
  consentUrl: string
  expiresAt: string
  pollIntervalMs: number
  /** keep this in memory only; it is what turns an approval into a token */
  verifier: string
}

export interface Token {
  id: string
  /** the bearer secret (`tsp_…`), shown once */
  secret: string
  label: string
  caveats: Caveats
  expiresAt: string
}

export type ExchangeOutcome =
  | { status: 'pending'; pollIntervalMs?: number }
  | ({ status: 'ready' } & Token)
  | { status: 'denied' }
  | { status: 'refused'; reason?: string; problems?: string[] }

/** PKCE pair: a random verifier and the base64url SHA-256 the host sees. */
export async function pkce(
  random: (bytes: number) => Uint8Array = randomBytes
): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64url(random(32))
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  )
  return { verifier, challenge: base64url(new Uint8Array(digest)) }
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n)
  crypto.getRandomValues(out)
  return out
}

export function base64url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function post(
  host: string,
  action: string,
  body: unknown,
  fetchImpl: typeof fetch
): Promise<{
  status: number
  json: Record<string, unknown> | null
  text: string
}> {
  const res = await fetchImpl(
    `${host.replace(/\/$/, '')}/authorize?action=${action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )
  const text = await res.text()
  // a site's address given as the host: say so in one line, never dump the page
  if (isWebPage(res.headers.get('content-type'), text))
    throw new Error(`virta: ${host} — ${webPageDiagnosis(res.status)}`)
  try {
    return { status: res.status, json: JSON.parse(text), text }
  } catch {
    return { status: res.status, json: null, text }
  }
}

/** Step one: ask the host for a consent URL. The verifier stays here. */
export async function startAuthorization(
  options: AuthorizeOptions
): Promise<StartedAuthorization> {
  const fetchImpl = options.fetch ?? fetch
  const { verifier, challenge } = await pkce()
  const started = await post(
    options.host,
    'start',
    {
      label: options.label,
      caveats: options.caveats,
      codeChallenge: challenge,
      mode: options.mode,
      ...(options.redirectPort ? { redirectPort: options.redirectPort } : {}),
      ...(options.ttlMs ? { ttlMs: options.ttlMs } : {}),
    },
    fetchImpl
  )
  if (started.status !== 200 || started.json?.status !== 'started') {
    throw new Error(
      `virta: could not start authorization (${started.status}): ${
        started.text.slice(0, 200) || 'no body'
      }`
    )
  }
  const j = started.json
  return {
    requestId: String(j.requestId),
    consentUrl: String(j.consentUrl),
    expiresAt: String(j.expiresAt),
    pollIntervalMs: Number(j.pollIntervalMs) || 2000,
    verifier,
  }
}

/** Step two: one exchange attempt. `pending` until the human approves. */
export async function exchange(
  host: string,
  started: Pick<StartedAuthorization, 'requestId' | 'verifier'>,
  fetchImpl: typeof fetch = fetch
): Promise<ExchangeOutcome> {
  const res = await post(
    host,
    'exchange',
    { requestId: started.requestId, verifier: started.verifier },
    fetchImpl
  )
  const j = res.json
  if (!j) return { status: 'refused', reason: res.text.slice(0, 200) }
  if (j.status === 'ready') {
    return {
      status: 'ready',
      id: String(j.id),
      secret: String(j.secret),
      label: String(j.label),
      caveats: j.caveats as Caveats,
      expiresAt: String(j.expiresAt),
    }
  }
  if (j.status === 'pending') {
    return {
      status: 'pending',
      pollIntervalMs: Number(j.pollIntervalMs) || undefined,
    }
  }
  if (j.status === 'denied') return { status: 'denied' }
  return {
    status: 'refused',
    reason: typeof j.reason === 'string' ? j.reason : undefined,
    problems: Array.isArray(j.problems) ? (j.problems as string[]) : undefined,
  }
}

/**
 * Poll the exchange until the token is ready, the request is denied or
 * refused, or the deadline passes. `sleep` is injectable for tests.
 */
export async function waitForToken(
  host: string,
  started: StartedAuthorization,
  options: {
    deadlineMs?: number
    fetch?: typeof fetch
    sleep?: (ms: number) => Promise<void>
    now?: () => number
  } = {}
): Promise<Token> {
  const fetchImpl = options.fetch ?? fetch
  const sleep =
    options.sleep ??
    ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const now = options.now ?? Date.now
  const deadline = now() + (options.deadlineMs ?? 10 * 60_000)
  for (;;) {
    const outcome = await exchange(host, started, fetchImpl)
    if (outcome.status === 'ready') {
      const { status, ...token } = outcome
      void status
      return token
    }
    if (outcome.status === 'denied')
      throw new Error('virta: the authorization request was denied')
    // the host says only "refused" at the exchange; past the request's own
    // expiry, that is what it means — and the fix is a new login (owner,
    // 2026-10-02: a remote approval landed after the 10 minutes)
    const expiry = Date.parse(started.expiresAt)
    if (
      outcome.status === 'refused' &&
      Number.isFinite(expiry) &&
      now() >= expiry
    )
      throw new Error(
        'virta: the authorization request expired before it was approved (requests last 10 minutes): run the login again for a new link'
      )
    if (outcome.status === 'refused')
      throw new Error(
        `virta: the authorization request was refused${
          outcome.reason ? `: ${outcome.reason}` : ''
        }${outcome.problems?.length ? `: ${outcome.problems.join('; ')}` : ''}`
      )
    if (now() > deadline)
      throw new Error('virta: timed out waiting for approval')
    await sleep(outcome.pollIntervalMs ?? started.pollIntervalMs)
  }
}

/**
 * The consent URL `virta login` may hand to the OS opener, or null: it must
 * be https, on the host's own origin, and made only of the characters RFC
 * 3986 allows — so no opener ever sees a shell metacharacter or whitespace,
 * whatever the host answered. Anything else is printed for a person to
 * open by hand. (0.5.0 re-review, M2: `cmd /c start` treats `&` as a
 * separator; the check is defence in depth, the opener must not be a shell.)
 */
export function consentUrlToOpen(
  host: string,
  consentUrl: string
): string | null {
  let url: URL
  let origin: string
  try {
    url = new URL(consentUrl)
    origin = new URL(host).origin
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.origin !== origin) return null
  // narrower than RFC 3986: no `$ ( ) ; ' * ! ,` — an old xdg-open (before
  // xdg-utils 1.1.3, CVE-2017-18266) substituted the URL into a user's
  // $BROWSER unescaped; the opener is not a shell, this is depth
  if (!/^[A-Za-z0-9\-._~:/?=&%+]+$/.test(url.href)) return null
  return url.href
}

/**
 * Whether a request to `virta login`'s loopback listener is the approval it
 * is waiting for: the `request` query parameter must equal the id the
 * host handed us at start — never an empty expectation, never a stray
 * probe or prefetch. Everything else is answered 404 and ignored.
 */
export function redirectAccepted(
  url: string | URL,
  expectedRequestId: string
): boolean {
  if (!expectedRequestId) return false
  try {
    return (
      new URL(String(url), 'http://127.0.0.1').searchParams.get('request') ===
      expectedRequestId
    )
  } catch {
    return false
  }
}
